// ink-component
import React, { Fragment, ReactNode, RefObject, useEffect, useState } from 'react';
import { ThemeProvider } from 'styled-components';
import getGlobalProps from '../../helpers/constants/global-props';
import SelectableListOption from './SelectableListOption/SelectableListOption';
import { SelectableListWrapper, SelectableListOptionContent } from './styles';
import useWindowWidth from '../../hooks/useWindowWidth';

interface Meta {
  initial: string;
}

interface Option {
  id: string | number;
  value: string | number | boolean;
  content: ReactNode;
  moreContent?: ReactNode;
  isInvalid?: boolean;
  isDisabled?: boolean;
  isHorizontal?: boolean;
  externalRef?: RefObject<HTMLInputElement>;
  'data-testid'?: string | number;
  'data-track-name'?: string;
  'data-track-payload'?: any;
  'data-track-events'?: string[];
}

export interface SelectableListProps {
  /** Radio identifier */
  id: string;
  /** Array that will be used to create each List Option */
  options: Array<Option>;
  /** External control and initial value.
   * Keep in mind that multiSelects are Checkboxes, so the value should be defined in the `options` array.  */
  value?: string;
  /** onChange callback. Passes the Event */
  // eslint-disable-next-line @typescript-eslint/ban-types
  onChange?: Function;
  /** onClick callback. Passes the Event.
   * **Attention:** you should always give preference to the onChange event
   * because it is triggered by both mouse and keyboard events and is more
   * reliable than the onClick for value control. Always use the onClick callback
   * in combination with the onChange, and **just if you need to execute a
   * function if the current value is clicked again.**
   * */
  // eslint-disable-next-line @typescript-eslint/ban-types
  onClick?: Function;
  /** HTML type */
  autoFocus?: boolean;
  /** Horizontal version */
  isHorizontal?: boolean;
  /** Modifier for multi selects that use Checkboxes */
  multiSelect?: boolean;
  /** Controls internal spacing */
  spacing?: 'compact' | 'gutter';
  /** Marks the element with a given ID to make it easier to select in tests */
  'data-testid'?: string | number;
  /** @ignore */
  meta?: Meta;
}

const globalProps = getGlobalProps({});

/**
 * @purpose Select one answer from a detailed list of options
 * @categories form
 * @aka card radio option
 */
function SelectableList(props: SelectableListProps) {
  const { isMobile } = useWindowWidth();
  const [value, setValue] = useState<string | number | undefined>(props.value || props.meta?.initial);

  useEffect(() => {
    setValue(props.value);
  }, [props.value]);

  const handleChange = e => {
    props.onChange && props.onChange(e);
    setValue(e.target.value);
  };

  const handleClick = e => {
    props.onClick && props.onClick(e);
  };

  const renderOptions = () =>
    props.options.map(option => (
      <Fragment key={option.id}>
        <SelectableListOption
          id={String(option.id)}
          value={option.value}
          content={option.content}
          isInvalid={option.isInvalid}
          isDisabled={option.isDisabled}
          name={props.id}
          isChecked={option.value === value}
          multiSelect={props.multiSelect}
          onChange={handleChange}
          onClick={handleClick}
          autoFocus={props.autoFocus}
          isHorizontal={props.isHorizontal}
          data-testid={option['data-testid']}
          data-track-name={option['data-track-name']}
          data-track-payload={option['data-track-payload']}
          data-track-events={option['data-track-events']}
        />
        {option.value === value && option.moreContent && (
          <SelectableListOptionContent>{option.moreContent}</SelectableListOptionContent>
        )}
      </Fragment>
    ));

  return (
    <ThemeProvider
      theme={currentTheme => ({
        ...currentTheme,
        selectableListHorizontal: props.isHorizontal && !isMobile,
        selectableListSpacing: props.spacing,
      })}
    >
      <SelectableListWrapper {...globalProps} data-testid={props['data-testid']}>
        {renderOptions()}
      </SelectableListWrapper>
    </ThemeProvider>
  );
}

SelectableList.defaultProps = {
  onChange: () => {},
  onClick: () => {},
  autoFocus: false,
  isHorizontal: false,
  multiSelect: false,
  spacing: 'gutter',

  meta: {},
};

SelectableList.displayName = 'SelectableList';

export { SelectableList as SelectableListDefault };
export { Option as SelectableListOptionProps };

export default SelectableList;
